import requestWithSubTenantIfNeeded from "store/api/organization/requestWithSubTenantIfNeeded";
import { request as genericQueryPostRequest } from "store/api/organization/genericQueryPost/genericQueryPostRequest";
import { GENERIC_QUERY_DATA_LIMIT } from "@p81-common/shell-core-js";
import type { GQSResult } from "types";
import type { Role } from "../RolesDataGrid/types";

export interface PermissionCategory {
  id: string;
  /** Stable i18n key (e.g. "dashboards", "active_sessions"). */
  name: string;
  /** English label from the BE — used as fallback when no translation exists. */
  displayName: string;
  /** Stable i18n key for the group (e.g. "policy_management"). */
  groupName: string;
  /** English group label from the BE — fallback when no translation exists. */
  groupDisplayName: string;
  sortOrder: number;
  groupSortOrder?: number;
  /** Permission IDs grantable via read access; empty means read isn't supported. */
  readPermissions: string[];
  /** Permission IDs grantable via manage access; empty means manage isn't supported. */
  managePermissions: string[];
}

export interface RoleBody {
  name: string;
  description?: string;
  categories: { categoryId: string; access: "read" | "manage" }[];
}

const PERMISSION_CATEGORY_FIELDS = [
  "id",
  "name",
  "displayName",
  "groupName",
  "groupDisplayName",
  "sortOrder",
  "groupSortOrder",
  "readPermissions",
  "managePermissions",
];

export const createCustomRole = (body: RoleBody): Promise<Role> =>
  requestWithSubTenantIfNeeded("post", "gum/custom-roles", { json: body }).then(
    (res: { role: Role }) => res.role
  );

export const updateCustomRole = (id: string, body: RoleBody): Promise<Role> =>
  requestWithSubTenantIfNeeded("put", `gum/custom-roles/${id}`, {
    json: body,
  }).then((res: { role: Role }) => res.role);

export const getCustomRole = (id: string): Promise<Role> =>
  requestWithSubTenantIfNeeded("get", `gum/custom-roles/${id}`).then(
    (res: { role: Role }) => res.role
  );

export const deleteCustomRole = (id: string): Promise<void> =>
  requestWithSubTenantIfNeeded("delete", `gum/custom-roles/${id}`);

/**
 * The "Base Category" entry is an internal catch-all used by the BE for
 * legacy access mapping. It must be kept on the form values (so it
 * round-trips back to the server on save) but never shown in the UI and
 * never counted toward the "at least one permission" validation rule.
 *
 * Identified by the stable `name` key (snake_case slug from
 * `tasks/dbSeeds/permissionCategories.json`) — never by `displayName`,
 * which is a translatable label and would break on locale change.
 */
export const HIDDEN_CATEGORY_NAME = "base_category";

export const isHiddenCategory = (c: { name: string }): boolean =>
  c.name === HIDDEN_CATEGORY_NAME;

export function getHiddenCategoryIds(
  categories: PermissionCategory[]
): Set<string> {
  const ids = new Set<string>();
  categories.forEach((c) => {
    if (isHiddenCategory(c)) ids.add(c.id);
  });
  return ids;
}

/**
 * `requireTenantId: false` is intentional — `PermissionCategory` is system-
 * global metadata (the same catalog for every tenant), defined in the BE
 * seed file `tasks/dbSeeds/permissionCategories.json`. There is no tenant-
 * scoped variant, so attaching `x-tenantId` would be a no-op at best and
 * would block the call in tenant-switching flows where the header isn't yet
 * resolved.
 *
 * `GENERIC_QUERY_DATA_LIMIT` is the shared cap used by every GQS-backed
 * "fetch-all" call in the app. Current catalog is ~23 entries; if it ever
 * grows past the limit the BE would silently truncate — warn here so it's
 * surfaced in CI/logs instead of as a silent UI bug.
 */
export const getPermissionCategories = (): Promise<PermissionCategory[]> =>
  genericQueryPostRequest({
    entityName: "PermissionCategory",
    page: 1,
    itemsPerPage: GENERIC_QUERY_DATA_LIMIT,
    fields: PERMISSION_CATEGORY_FIELDS,
    sort: { field: "sortOrder", direction: "asc" },
    requireTenantId: false,
  }).then((res: GQSResult<PermissionCategory>) => {
    const data = res.data ?? [];
    if (data.length >= GENERIC_QUERY_DATA_LIMIT) {
      // eslint-disable-next-line no-console
      console.warn(
        `getPermissionCategories: hit the ${GENERIC_QUERY_DATA_LIMIT}-item cap — categories list is truncated. Switch to pagination.`
      );
    }
    return data;
  });
